import { useEffect, useState } from 'react'
import { fetchAccountAiFeatures, FeaturesHttpError } from '@/shared/auth/features'

export type AiFeatureStatus = 'checking' | 'enabled' | 'disabled'

export interface AiFeatureGate {
  /** Whole-add-in gate on the account's aiAgents flag. */
  status: AiFeatureStatus
  /** Whether the checklist-scan view (aiChecklist flag) is available. */
  checklistEnabled: boolean
}

/**
 * Post-login gate on the account's AI feature flags. `status` resolves
 * 'disabled' only when the backend affirmatively reports aiAgents off;
 * transport/server errors fail open since every AI endpoint enforces the
 * flags server-side anyway. A 401 means the stored token is stale -> sign out.
 */
export function useAiFeatureGate(token: string, onUnauthorized: () => void): AiFeatureGate {
  const [gate, setGate] = useState<AiFeatureGate>({ status: 'checking', checklistEnabled: false })

  useEffect(() => {
    let cancelled = false
    setGate({ status: 'checking', checklistEnabled: false })
    fetchAccountAiFeatures(token)
      .then((features) => {
        if (cancelled) return
        setGate({
          status: features.aiAgents ? 'enabled' : 'disabled',
          checklistEnabled: features.aiChecklist
        })
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof FeaturesHttpError && e.statusCode === 401) {
          onUnauthorized()
          return
        }
        setGate({ status: 'enabled', checklistEnabled: true })
      })
    return () => {
      cancelled = true
    }
  }, [token, onUnauthorized])

  return gate
}
